import React from 'react';
import { withTranslation } from 'react-i18next';
import { SimpleLineChart } from '../charts/SimpleLineChart';
import { BudgetHistoryRow } from './BudgetHistoryRow';

const MONTHS_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTHS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatDateShort(dateStr, language) {
    if (!dateStr) return '';
    const parts = dateStr.split('/');
    if (parts.length !== 3) return dateStr;
    const monthIndex = parseInt(parts[1], 10) - 1;
    const yearShort = parts[2].slice(-2);
    const months = language === 'es' ? MONTHS_ES : MONTHS_EN;
    return `${months[monthIndex]}/${yearShort}`;
}

function formatMoney(value) {
    if (value === null || value === undefined) return '-';
    return '$ ' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

class BudgetHistoryPanelComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = { expandedIds: new Set() };
        this.handleToggle = this.handleToggle.bind(this);
    }

    handleToggle(id) {
        this.setState(prev => {
            const next = new Set(prev.expandedIds);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return { expandedIds: next };
        });
    }

    getChartData() {
        const { t, i18n, budgetHistory } = this.props;
        if (!budgetHistory || budgetHistory.length === 0) return [];

        const reversed = [...budgetHistory].reverse();
        const sliced = reversed.slice(-24);

        return sliced.map(budget => ({
            [t('business_unit_budget_history.year_month')]: formatDateShort(budget.from_date, i18n.language),
            [t('business_unit_budget_history.total_budget')]: budget.total_budget_raw,
            [t('business_unit_budget_history.cost')]: budget.cost_raw,
        }));
    }

    renderChart() {
        const { t } = this.props;
        const chartData = this.getChartData();
        if (chartData.length < 2) return null;

        const dimensionKey = t('business_unit_budget_history.year_month');
        const metricKeys = [
            t('business_unit_budget_history.total_budget'),
            t('business_unit_budget_history.cost'),
        ];

        return (
            <div style={{ margin: '0 -20px 10px' }}>
                <SimpleLineChart
                    data={chartData}
                    dimensionKey={dimensionKey}
                    metricKeys={metricKeys}
                    height={300}
                    tooltipFormatter={(value) => formatMoney(value)}
                />
            </div>
        );
    }

    renderRows() {
        const { i18n, budgetHistory } = this.props;
        const { expandedIds } = this.state;
        return budgetHistory.map((budget) => (
            <BudgetHistoryRow
                key={budget.id}
                budget={budget}
                formattedPeriod={formatDateShort(budget.from_date, i18n.language)}
                isExpanded={expandedIds.has(budget.id)}
                onToggle={this.handleToggle}
            />
        ));
    }

    render() {
        const { t, budgetHistory } = this.props;
        if (!budgetHistory || budgetHistory.length === 0) {
            return (
                <div className="budget-history-empty">
                    {t('business_unit_budget_history.no_budgets')}
                </div>
            );
        }

        return (
            <>
                {this.renderChart()}
                <div className="table-box budget-history">
                    <table className="table table-striped">
                        <thead>
                            <tr>
                                <th>{t('business_unit_budget_history.year_month')}</th>
                                <th>{t('business_unit_budget_history.fixed_budget')}</th>
                                <th>{t('business_unit_budget_history.previous_month_diff')}</th>
                                <th>{t('business_unit_budget_history.total_budget')}</th>
                                <th>{t('business_unit_budget_history.cost')}</th>
                                <th>{t('business_unit_budget_history.difference')}</th>
                                <th>{t('business_unit_budget_history.conversions')}</th>
                                <th>{t('business_unit_budget_history.goals')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.renderRows()}
                        </tbody>
                    </table>
                </div>
            </>
        );
    }
}

export const BudgetHistoryPanel = withTranslation()(BudgetHistoryPanelComponent);
